import { IonContent, IonHeader, IonImg, IonPage, IonTextarea, IonButton, useIonAlert, IonItem } from "@ionic/react"
import AppHeader from "../../../components/AppHeader/AppHeader"
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../services/redux/hook";
import { UserInfo, WalletInfoState, Sep10ChallengeParams, DelegateSigningParams } from "../../../types/types";
import './SecureWalletInfo.css';
import { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router";
import { ebioroApi, useDelegateSigningMutation, useGetAccountRecoveryPartialTokenQuery } from "../../../services/api/ebioro/ebioro";
import { CubeSignerClient, envs, IdentityProof } from "@cubist-labs/cubesigner-sdk";
import { Networks } from "@stellar/stellar-sdk";
import { stellarApi } from "../../../services/api/stellar";
import { receivedSep10Token } from "../../../services/redux/slices/auth/sep10";
import { Preferences } from '@capacitor/preferences';
import * as Sentry from "@sentry/react";
import { addSigner } from "../../../services/stellar/TransactionBuilder";
import { createSep7URI } from "../../../services/stellar/sep7";
import { useTransactionTracker } from "../../../hooks/useTransactionTracker";

const ebioroAuthDomain = import.meta.env.VITE_API_AUTH_DOMAIN || '';
const isTestnet = import.meta.env.VITE_isTestnet === 'true' ? true : false;
const cubesignerEnv = isTestnet ? envs.gamma : envs.prod;
const cubesignerOrgId = import.meta.env.VITE_CUBESIGNER_ORG_ID;

const SecureWalletInfoPage: React.FC<any> = () => {

    const { t, i18n } = useTranslation();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const walletInfo: WalletInfoState = useAppSelector((state) => state.walletInfo);

    const user: UserInfo = useAppSelector((state) => state.user);
    const [authToken, setAuthToken] = useState<string>();
    const [signature, setSignature] = useState<string>('');
    const [present] = useIonAlert();
    const [isSecuring, setIsSecuring] = useState(false);
    const [sep10Token, setSep10Token] = useState<any>(null);
    const [delegateSigning, { isSuccess: isDelegateSuccess }] = useDelegateSigningMutation();
  
    // Transaction tracker hook
    const {
        isTracking,
        startTracking,
    } = useTransactionTracker(undefined, {
        callbacks: {
            onCompleted: () => {
                // Navigate to init page after successful completion
                setTimeout(() => {
                    setIsSecuring(false); 
                    goToInitPage();
                }, 2000); // Small delay to show success message
            },
            onError: (error) => {
                // Reset securing state so user can retry
                setIsSecuring(false);
                
                // Show appropriate error message
                if (error === 'submission_error') {
                    presentErrorAlert({ data: t('transaction.submission.ERROR') });
                } else if (error === 'transaction_error') {
                    presentErrorAlert({ data: t('transaction.processing.ERROR') || 'Transaction failed to process. Please try again.' });
                } else {
                    presentErrorAlert({ data: t('application.uncaught.ERROR') });
                }
            },
            onSurgePricing: () => {
            }
        }
    });

    const goToInitPage = async () => {

        try {

            setIsSecuring(false);

            history.push('/init');


        } catch (error) {

            Sentry.captureException(error);
            setIsSecuring(false);
            presentErrorAlert({ data: t('application.uncaught.ERROR') });
            return

        }

    }


    useEffect(() => {
        const location: any = history.location;
        const authToken = location.state.detail.auth_token;
        setAuthToken(authToken);
    }, []);

    // Get SEP-10 token when component mounts and wallet info is available
    useEffect(() => {
        if (walletInfo.account_key && walletInfo.device_private_key && !sep10Token) {
            getSep10Token();
        }
    }, [walletInfo.account_key, walletInfo.device_private_key]);

    const presentErrorAlert = (error: any) => {
        present({
            header: t('Error'),
            message: `${error.data}`,
            buttons: [
                {
                    text: t('Close'),
                    role: 'cancel',
                    handler: () => {
                        return
                    }
                }
            ],
        })
    };

    // Trigger the API call when we have both email and authToken
    const {
        data: recoveryTokenData,
        error: recoveryTokenError,
        isLoading: isRecoveryTokenLoading,
        refetch
    } = useGetAccountRecoveryPartialTokenQuery(
        {
            email: user.email,
            auth_token: authToken || ''
        },
        {
            // Only make the API call when we have both required parameters
            skip: !user.email || !authToken,
            // Refetch on mount to ensure fresh token
            refetchOnMountOrArgChange: true
        }
    );

    //Handle API errors
    useEffect(() => {
        if (recoveryTokenError) {
            presentErrorAlert({ data: t('registration.secure.wallet.ERROR_SEND_FAILED') });
        }
    }, [recoveryTokenError, t]);

    //Handle resend functionality
    const handleResendCode = () => {
        if (user.email && authToken) {
            setSignature(''); // Clear the current signature input
            refetch(); // This will trigger a new API call to send another verification code
        }
    };

    // Handle signature input change
    const handleSignatureChange = (event: CustomEvent) => {
        const value = event.detail.value || '';
        setSignature(value.trim());
    };

    // Get SEP-10 token for Stellar authentication
    const getSep10Token = useCallback(async (): Promise<any> => {
        if (!walletInfo.account_key || !walletInfo.device_private_key) {
            presentErrorAlert({ data: t('Missing wallet information. Please try again.') });
            return null;
        }

        const params: Sep10ChallengeParams = {
            account: walletInfo.account_key,
            home_domain: ebioroAuthDomain,
            network: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
            privateKey: walletInfo.device_private_key
        };

        try {
            const sep10Data = await dispatch(stellarApi.endpoints.sep10.initiate(params)).unwrap();

            if (sep10Data) {
                dispatch(receivedSep10Token(sep10Data));
                await Preferences.set({
                    key: ebioroAuthDomain,
                    value: JSON.stringify(sep10Data),
                });

                setSep10Token(sep10Data);
                return sep10Data;
            }

            return null;
        } catch (error) {
            console.error('SEP-10 authentication failed:', error);
            presentErrorAlert({ data: t('Failed to authenticate with Stellar network. Please try again.') });
            return null;
        }
    }, [walletInfo.account_key, walletInfo.device_private_key, dispatch, t]);

    // Handle verification - reconstruct OIDC token and verify
    const secureWallet = async () => {

        if (!signature.trim()) {
            presentErrorAlert({ data: t('registration.secure.wallet.ERROR_NO_TOKEN') });
            return;
        }

        if (!recoveryTokenData?.partial_token) {
            presentErrorAlert({ data: t('registration.secure.wallet.ERROR_NO_DATA') });
            return;
        }

        setIsSecuring(true);

        try {
            // Step 1: Get SEP-10 token for Stellar authentication
            const token = sep10Token || await getSep10Token();

            if (!token) {
                setIsSecuring(false);
                return; // Error already handled in getSep10Token
            }

            // Step 2: Reconstruct the complete OIDC token
            const completeOidcToken = recoveryTokenData.partial_token + signature.trim();

            // Step 3: Get CubeSigner identity proof
            const identityId = await getCubsignerIdentity(completeOidcToken);

            if (!identityId) {
                setIsSecuring(false);
                return; // Error already handled in getCubsignerIdentity function
            }

            // Step 4: Create wallet with both SEP-10 token and CubeSigner identity
            const external_id = user.id;


            const result = await dispatch(ebioroApi.endpoints.createWallet.initiate({
                chain: 'stellar',
                identityProof: identityId,
                external_id: external_id,
                account_key: walletInfo.account_key as string
            })).unwrap();

            const address = result.address;

            const [error, tx] = await addSigner(walletInfo.account_key as string, walletInfo.device_private_key as string, address, 20);

            if (error) {
                throw error;
            }

            if (tx) {

                const uri = createSep7URI(tx, walletInfo.account_key as string);

                if (!uri) {

                    presentErrorAlert({ data: t('registration.secure.wallet.ERROR_URI_CREATION') });
                    setIsSecuring(false);
                    return

                }

                try {

                    let params: DelegateSigningParams = {
                        uri: uri,
                        account: walletInfo.account_key as string,
                        name: 'Wallet Backup',
                        msg: 'Wallet backup transaction',
                    }

                    const response = await delegateSigning(params).unwrap();
                    const stellarTxId = response.stellar_transaction_id as string;

                    await startTracking(stellarTxId);
                    
                } catch (error: any) {

                    Sentry.captureException(error);

                    if (error.message === 'submission_error') {
                        presentErrorAlert({ data: t('transaction.submission.ERROR') });
                    } else {
                        presentErrorAlert({ data: t('application.uncaught.ERROR') });
                    }

                    setIsSecuring(false);
                    return;

                }

            } else {
                // If no transaction was created, there's an error with transaction building
                presentErrorAlert({ data: t('application.uncaught.ERROR') });
                setIsSecuring(false);
                return;
            }

            // Transaction tracking is now active, let the tracker handle navigation

        } catch (error) {
            setIsSecuring(false);
            //setShowTransactionTracking(false); // Ensure transaction tracking is not shown on error
            presentErrorAlert({ data: t('Failed to secure your wallet. Please try again.') });
            Sentry.captureException(error);
            return; // Explicit return to prevent any further execution
        }
    };

    const getCubsignerIdentity = async (oidcToken: string): Promise<IdentityProof | null> => {
        // Validate required parameters
        if (!cubesignerEnv || !cubesignerOrgId || !oidcToken) {
            presentErrorAlert({
                data: t('Missing required CubeSigner configuration. Please check your environment settings.')
            });
            return null;
        }

        try {

            const identity_proof = await CubeSignerClient.proveOidcIdentity(
                cubesignerEnv,
                cubesignerOrgId,
                oidcToken
            );

            return identity_proof;

        } catch (error) {

            const errorMessage = error instanceof Error
                ? error.message
                : t('Failed to retrieve CubeSigner identity. Please try again.');

            presentErrorAlert({ data: errorMessage });
            return null;
        }
    };



    return (
        <IonPage>
            
            <IonHeader className="ion-no-border">

                <IonItem>

                </IonItem>
                <AppHeader
                    title={t('registration.secure.wallet.HEADER') as string}
                >
                </AppHeader>

            </IonHeader>

            <IonContent scrollY={false}>

                <div className="content-container">
         
                        <>
                            {/* Hero Section with Icon */}
                            <div className="hero-section">
                                <div className="icon-container">
                                    <div className="icon-background">
                                        <IonImg
                                            src={'/assets/img/safety.svg'}
                                            className="safety-icon"
                                        />
                                    </div>
                                    <div className="success-badge">
                                        <svg className="success-icon" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>

                                <p className="page-description">
                                    {t('registration.secure.wallet.DESC_PART1')}{' '}
                                    <span className="email-highlight">{user.email}</span>.{' '}
                                    {t('registration.secure.wallet.DESC_PART2')}.
                                </p>
                            </div>

                            {/* Verification Code Input */}
                            <div className="input-card">
                                <div className="input-section">
                                    <label className="input-label">
                                        {t('registration.secure.wallet.TOKEN_LABEL')}
                                    </label>
                                    <IonTextarea
                                        fill="outline"
                                        placeholder={t('registration.secure.wallet.TOKEN_PLACEHOLDER') as string}
                                        mode="md"
                                        autoGrow={true}
                                        className="verification-input"
                                        rows={3}
                                        value={signature}
                                        onIonInput={handleSignatureChange}
                                    />
                                </div>

                                <div className="resend-section">
                                    <p className="resend-text">
                                        {t('registration.secure.wallet.RESEND_TEXT')}{' '}
                                        <button
                                            className="resend-button"
                                            onClick={handleResendCode}
                                            disabled={isRecoveryTokenLoading}
                                        >
                                            {isRecoveryTokenLoading ? t('registration.secure.wallet.SENDING') : t('registration.secure.wallet.RESEND_BTN')}
                                        </button>
                                    </p>
                                </div>
                            </div>

                            {/* Action Button */}
                            <div className="button-container">
                                <IonButton
                                    expand="block"
                                    className="verify-button"
                                    color="primary"
                                    onClick={secureWallet}
                                    disabled={!signature.trim() || !recoveryTokenData?.partial_token || isSecuring || isTracking}
                                >
                                    {(isSecuring || isTracking) ? t('registration.secure.wallet.BTN_SECURING') : t('registration.secure.wallet.BTN_VERIFY')}
                                </IonButton>
                            </div>
                        </>
                   
                </div>
            </IonContent>
            

        </IonPage>
    )
}

export default SecureWalletInfoPage